/*
💬 BeeNetwork Messaging System
GetMyHive Operations Center - Mesaj Yönetim ve İzleme Sistemi
*/

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Input,
  Select,
  Table,
  Tag,
  Avatar,
  Badge,
  List,
  Timeline,
  Modal,
  Form,
  Tabs,
  Progress,
  Alert,
  Divider,
  Tooltip,
  Switch,
  DatePicker,
  Radio,
  Checkbox,
  Empty,
  Spin,
  Dropdown,
  Menu,
  Upload,
  Drawer
} from 'antd';
import {
  MessageOutlined,
  UserOutlined,
  SendOutlined,
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  BellOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  RobotOutlined,
  BarChartOutlined,
  SettingOutlined,
  PlusOutlined,
  CommentOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  PhoneOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

// Mock BeeNetwork Messages Data
const mockMessages = [
  {
    id: '1',
    type: 'emergency_alert',
    sender: 'Ayşe Demir',
    senderId: 'user_001',
    hive: 'Kadıköy-Merkez-H1',
    location: 'Kadıköy, İstanbul',
    content: '🚨 Acil durum: Bağdat Caddesi üzerinde trafik kazası. Ambulans yolda.',
    timestamp: '2024-01-16 18:45',
    status: 'active',
    priority: 'critical',
    recipients: 45,
    responses: 12,
    category: 'emergency',
    isVerified: true,
    coordinates: { lat: 40.9827, lng: 29.0375 }
  },
  {
    id: '2',
    type: 'safety_warning',
    sender: 'Mehmet Kaya',
    senderId: 'user_002',
    hive: 'Şişli-Kuzey-H3',
    location: 'Şişli, İstanbul',
    content: '⚠️ Dikkat: Mecidiyeköy metro çıkışında şüpheli kişi görüldü. Dikkatli olun.',
    timestamp: '2024-01-16 17:30',
    status: 'resolved',
    priority: 'high',
    recipients: 78,
    responses: 23,
    category: 'safety',
    isVerified: true,
    coordinates: { lat: 41.0686, lng: 28.9944 }
  },
  {
    id: '3',
    type: 'community_help',
    sender: 'Zeynep Özkan',
    senderId: 'user_003',
    hive: 'Beşiktaş-Sahil-H2',
    location: 'Beşiktaş, İstanbul',
    content: '🤝 Yardım: Yaşlı komşum için market alışverişi yapacak birini arıyorum.',
    timestamp: '2024-01-16 16:15',
    status: 'pending',
    priority: 'medium',
    recipients: 34,
    responses: 8,
    category: 'community',
    isVerified: false,
    coordinates: { lat: 41.0422, lng: 29.0067 }
  }
];

// Mock Hive Statistics
const mockHiveStats = [
  { hive: 'Kadıköy-Merkez-H1', members: 245, messages: 1247, emergencies: 12, activeUsers: 89 },
  { hive: 'Şişli-Kuzey-H3', members: 189, messages: 892, emergencies: 8, activeUsers: 67 },
  { hive: 'Beşiktaş-Sahil-H2', members: 167, messages: 634, emergencies: 5, activeUsers: 45 }
];

const MessagingPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [messages, setMessages] = useState(mockMessages);
  const [filteredMessages, setFilteredMessages] = useState(mockMessages);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [messageDetailModal, setMessageDetailModal] = useState(false);
  const [composeModal, setComposeModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [hiveFilter, setHiveFilter] = useState('all');
  const [form] = Form.useForm();

  // Statistics
  const [stats, setStats] = useState({
    totalMessages: 4892,
    activeAlerts: 23,
    emergencyMessages: 45,
    responseRate: 87.3
  });

  useEffect(() => {
    loadMessagingData();
  }, []);

  useEffect(() => {
    filterMessages();
  }, [messages, searchTerm, categoryFilter, priorityFilter, statusFilter, hiveFilter]);

  const loadMessagingData = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Messaging data loaded');
    } catch (error) {
      console.error('Messaging loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMessages = () => {
    let filtered = messages;

    if (searchTerm) {
      filtered = filtered.filter(msg =>
        msg.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
        msg.hive.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(msg => msg.category === categoryFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(msg => msg.priority === priorityFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(msg => msg.status === statusFilter);
    }

    if (hiveFilter !== 'all') {
      filtered = filtered.filter(msg => msg.hive === hiveFilter);
    }

    setFilteredMessages(filtered);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#fadb14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#52c41a';
      case 'resolved': return '#1890ff';
      case 'investigating': return '#fa8c16';
      case 'pending': return '#fadb14';
      default: return '#d9d9d9';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'emergency': return <ExclamationCircleOutlined />;
      case 'safety': return <SafetyCertificateOutlined />;
      case 'community': return <TeamOutlined />;
      case 'harassment': return <WarningOutlined />;
      case 'info': return <BellOutlined />;
      default: return <MessageOutlined />;
    }
  };

  const openMessageDetail = (message: any) => {
    setSelectedMessage(message);
    setMessageDetailModal(true);
  };

  const sendBroadcastMessage = async (values: any) => {
    setLoading(true);
    try {
      console.log('Sending broadcast:', values);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newMessage = {
        id: Date.now().toString(),
        type: 'admin_broadcast',
        sender: 'Operations Center',
        senderId: 'admin_001',
        hive: values.targetHive || 'Tüm Hive\'ler',
        location: 'Operations Center',
        content: values.message,
        timestamp: new Date().toLocaleString('tr-TR'),
        status: 'active',
        priority: values.priority,
        recipients: values.targetHive === 'all' ? 1033 : 245,
        responses: 0,
        category: values.category,
        isVerified: true,
        coordinates: null
      };

      setMessages(prev => [newMessage, ...prev]);
      setComposeModal(false);
      form.resetFields();
    } catch (error) {
      console.error('Broadcast error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Messages Tab
  const renderMessages = () => (
    <div>
      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={6}>
            <Search
              placeholder="Mesaj ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Kategori"
              value={categoryFilter}
              onChange={setCategoryFilter}
            >
              <Option value="all">Tüm Kategoriler</Option>
              <Option value="emergency">Acil Durum</Option>
              <Option value="safety">Güvenlik</Option>
              <Option value="community">Topluluk</Option>
              <Option value="harassment">Taciz</Option>
              <Option value="info">Bilgi</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Öncelik"
              value={priorityFilter}
              onChange={setPriorityFilter}
            >
              <Option value="all">Tüm Öncelikler</Option>
              <Option value="critical">Kritik</Option>
              <Option value="high">Yüksek</Option>
              <Option value="medium">Orta</Option>
              <Option value="low">Düşük</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Durum"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="all">Tüm Durumlar</Option>
              <Option value="active">Aktif</Option>
              <Option value="resolved">Çözüldü</Option>
              <Option value="investigating">İnceleniyor</Option>
              <Option value="pending">Beklemede</Option>
            </Select>
          </Col>
          <Col xs={12} md={6}>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setComposeModal(true)}
              >
                Yayın Mesajı
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={loadMessagingData}
                loading={loading}
              >
                Yenile
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Messages List */}
      <List
        dataSource={filteredMessages}
        renderItem={(message) => (
          <List.Item
            style={{
              backgroundColor: message.priority === 'critical' ? 'rgba(255, 77, 79, 0.05)' : 'transparent',
              border: message.priority === 'critical' ? '1px solid rgba(255, 77, 79, 0.2)' : 'none',
              borderRadius: '6px',
              marginBottom: '8px',
              padding: '16px'
            }}
            actions={[
              <Button 
                size="small" 
                icon={<EyeOutlined />}
                onClick={() => openMessageDetail(message)}
              >
                Detay
              </Button>,
              <Button 
                size="small" 
                icon={<CommentOutlined />}
              >
                Yanıtla
              </Button>,
              <Dropdown
                menu={{
                  items: [
                    { key: 'flag', label: 'Bayrakla', icon: <WarningOutlined /> },
                    { key: 'delete', label: 'Sil', icon: <DeleteOutlined />, danger: true }
                  ]
                }}
              >
                <Button size="small" icon={<SettingOutlined />} />
              </Dropdown>
            ]}
          >
            <List.Item.Meta
              avatar={
                <Badge dot={message.status === 'active'}>
                  <Avatar 
                    icon={getCategoryIcon(message.category)}
                    style={{ backgroundColor: getPriorityColor(message.priority) }}
                  />
                </Badge>
              }
              title={
                <Space>
                  <Text strong>{message.sender}</Text>
                  <Tag color={getPriorityColor(message.priority)}>
                    {message.priority.toUpperCase()}
                  </Tag>
                  <Tag color={getStatusColor(message.status)}>
                    {message.status.toUpperCase()}
                  </Tag>
                  {message.isVerified && (
                    <Tag color="blue">VERİFİED</Tag>
                  )}
                </Space>
              }
              description={
                <Space direction="vertical" size="small">
                  <Text>{message.content}</Text>
                  <Space size="large">
                    <Text type="secondary">
                      <EnvironmentOutlined /> {message.hive}
                    </Text>
                    <Text type="secondary">
                      <ClockCircleOutlined /> {message.timestamp}
                    </Text>
                    <Text type="secondary">
                      <TeamOutlined /> {message.recipients} alıcı
                    </Text>
                    <Text type="secondary">
                      <CommentOutlined /> {message.responses} yanıt
                    </Text>
                  </Space>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );

  // Hive Statistics Tab
  const renderHiveStats = () => (
    <Row gutter={[16, 16]}>
      {mockHiveStats.map((hive, index) => (
        <Col xs={24} md={12} lg={8} key={index}>
          <Card
            title={
              <Space>
                <TeamOutlined />
                <span>{hive.hive}</span>
              </Space>
            }
            extra={
              <Badge 
                count={hive.activeUsers} 
                style={{ backgroundColor: '#52c41a' }}
                title="Aktif Kullanıcı"
              />
            }
          >
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Statistic title="Üye Sayısı" value={hive.members} />
              </Col>
              <Col span={12}>
                <Statistic title="Mesaj Sayısı" value={hive.messages} />
              </Col>
              <Col span={12}>
                <Statistic 
                  title="Acil Durum" 
                  value={hive.emergencies}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
              <Col span={12}>
                <Statistic 
                  title="Aktif Oran" 
                  value={((hive.activeUsers / hive.members) * 100).toFixed(1)}
                  suffix="%"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Analytics Tab
  const renderAnalytics = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={16}>
        <Card title="📊 Mesaj Trend Analizi">
          <Alert
            message="AI Analiz Sonuçları"
            description="Son 7 günde acil durum mesajları %23 artış gösterdi. En aktif hive: Kadıköy-Merkez-H1"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Timeline>
            <Timeline.Item color="red">
              <Text strong>Kritik Mesajlar:</Text> 45 adet (günlük ortalama: 6.4)
            </Timeline.Item>
            <Timeline.Item color="orange">
              <Text strong>Yüksek Öncelik:</Text> 123 adet (günlük ortalama: 17.6)
            </Timeline.Item>
            <Timeline.Item color="blue">
              <Text strong>Yanıt Oranı:</Text> %87.3 (hedef: %85)
            </Timeline.Item>
            <Timeline.Item color="green">
              <Text strong>Çözüm Süresi:</Text> Ortalama 12 dakika
            </Timeline.Item>
          </Timeline>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card title="🎯 Performans Metrikleri">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text type="secondary">Mesaj Yanıt Oranı</Text>
              <Progress percent={87} strokeColor="#52c41a" />
            </div>
            <div>
              <Text type="secondary">Acil Durum Müdahale</Text>
              <Progress percent={94} strokeColor="#1890ff" />
            </div>
            <div>
              <Text type="secondary">Hive Aktivitesi</Text>
              <Progress percent={76} strokeColor="#fa8c16" />
            </div>
            <div>
              <Text type="secondary">Sistem Sağlığı</Text>
              <Progress percent={98} strokeColor="#722ed1" />
            </div>
          </Space>
        </Card>
      </Col>
    </Row>
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Space>
            <MessageOutlined style={{ color: '#1890ff' }} />
            💬 BeeNetwork Mesaj Sistemi
          </Space>
        </Title>
        <Paragraph type="secondary">
          BeeNetwork dijital mesh ağı mesaj yönetimi, izleme ve analiz sistemi
        </Paragraph>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Mesaj"
              value={stats.totalMessages}
              prefix={<MessageOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aktif Uyarılar"
              value={stats.activeAlerts}
              prefix={<BellOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Acil Durum"
              value={stats.emergencyMessages}
              prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Yanıt Oranı"
              value={stats.responseRate}
              suffix="%"
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <Space>
              <MessageOutlined />
              Mesajlar
            </Space>
          } 
          key="messages"
        >
          {renderMessages()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <TeamOutlined />
              Hive İstatistikleri
            </Space>
          } 
          key="hive-stats"
        >
          {renderHiveStats()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <BarChartOutlined />
              Analitik
            </Space>
          } 
          key="analytics"
        >
          {renderAnalytics()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <SettingOutlined />
              Ayarlar
            </Space>
          } 
          key="settings"
        >
          <Card title="BeeNetwork Mesaj Ayarları">
            <Form layout="vertical">
              <Form.Item label="Otomatik Yanıt">
                <Switch defaultChecked /> Acil durum mesajlarına otomatik yanıt
              </Form.Item>
              <Form.Item label="Bildirim Ayarları">
                <Checkbox.Group defaultValue={['critical', 'high']}>
                  <Checkbox value="critical">Kritik mesajlar</Checkbox>
                  <Checkbox value="high">Yüksek öncelik</Checkbox>
                  <Checkbox value="medium">Orta öncelik</Checkbox>
                  <Checkbox value="low">Düşük öncelik</Checkbox>
                </Checkbox.Group>
              </Form.Item>
              <Form.Item label="Mesaj Filtreleme">
                <Switch defaultChecked /> AI tabanlı spam filtreleme
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
      </Tabs>

      {/* Message Detail Modal */}
      <Modal
        title="💬 Mesaj Detayları"
        open={messageDetailModal}
        onCancel={() => setMessageDetailModal(false)}
        footer={[
          <Button key="respond" type="primary" icon={<SendOutlined />}>
            Yanıtla
          </Button>,
          <Button key="close" onClick={() => setMessageDetailModal(false)}>
            Kapat
          </Button>
        ]}
        width={700}
      >
        {selectedMessage && (
          <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Space>
                  <Avatar 
                    icon={getCategoryIcon(selectedMessage.category)}
                    style={{ backgroundColor: getPriorityColor(selectedMessage.priority) }}
                    size="large"
                  />
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      {selectedMessage.sender}
                    </Title>
                    <Space>
                      <Tag color={getPriorityColor(selectedMessage.priority)}>
                        {selectedMessage.priority.toUpperCase()}
                      </Tag>
                      <Tag color={getStatusColor(selectedMessage.status)}>
                        {selectedMessage.status.toUpperCase()}
                      </Tag>
                      {selectedMessage.isVerified && (
                        <Tag color="blue">VERİFİED</Tag>
                      )}
                    </Space>
                  </div>
                </Space>
              </div>

              <Card size="small">
                <Paragraph>{selectedMessage.content}</Paragraph>
              </Card>

              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Statistic title="Alıcı Sayısı" value={selectedMessage.recipients} />
                </Col>
                <Col span={8}>
                  <Statistic title="Yanıt Sayısı" value={selectedMessage.responses} />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Yanıt Oranı" 
                    value={((selectedMessage.responses / selectedMessage.recipients) * 100).toFixed(1)}
                    suffix="%"
                  />
                </Col>
              </Row>

              <Card title="📍 Lokasyon Bilgileri" size="small">
                <Space direction="vertical">
                  <Text><EnvironmentOutlined /> Hive: {selectedMessage.hive}</Text>
                  <Text><GlobalOutlined /> Konum: {selectedMessage.location}</Text>
                  <Text><ClockCircleOutlined /> Zaman: {selectedMessage.timestamp}</Text>
                </Space>
              </Card>
            </Space>
          </div>
        )}
      </Modal>

      {/* Compose Broadcast Modal */}
      <Modal
        title="📢 Yayın Mesajı Gönder"
        open={composeModal}
        onCancel={() => setComposeModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={sendBroadcastMessage}
        >
          <Form.Item
            label="Hedef Hive"
            name="targetHive"
            rules={[{ required: true, message: 'Hedef hive seçin' }]}
          >
            <Select placeholder="Hive seçin">
              <Option value="all">Tüm Hive'ler</Option>
              <Option value="Kadıköy-Merkez-H1">Kadıköy-Merkez-H1</Option>
              <Option value="Şişli-Kuzey-H3">Şişli-Kuzey-H3</Option>
              <Option value="Beşiktaş-Sahil-H2">Beşiktaş-Sahil-H2</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Kategori"
            name="category"
            rules={[{ required: true, message: 'Kategori seçin' }]}
          >
            <Select placeholder="Kategori seçin">
              <Option value="emergency">Acil Durum</Option>
              <Option value="safety">Güvenlik</Option>
              <Option value="info">Bilgi</Option>
              <Option value="community">Topluluk</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Öncelik"
            name="priority"
            rules={[{ required: true, message: 'Öncelik seçin' }]}
          >
            <Radio.Group>
              <Radio value="critical">Kritik</Radio>
              <Radio value="high">Yüksek</Radio>
              <Radio value="medium">Orta</Radio>
              <Radio value="low">Düşük</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Mesaj"
            name="message"
            rules={[{ required: true, message: 'Mesaj yazın' }]}
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Yayın mesajınızı yazın..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<SendOutlined />}
              >
                Mesaj Gönder
              </Button>
              <Button onClick={() => setComposeModal(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MessagingPage;